import { useEffect, useState, useRef } from "react";
import { Spacer } from "@/components/Spacer";

import { motion, useInView } from "framer-motion";

export default function Section04({ isMobile }: { isMobile: boolean }) {
  const getDesktopDescription = () => {
    return (
      <div
        className="whitespace-pre-line text-center font-pre-medium text-[#2E323A] desktop:text-[20px] text-[14px] leading-[150%]"
        style={{ letterSpacing: "-0.02em" }}
      >
        CIERA는 실제 의료진의 사고 흐름을 모사하여 설계된 통합형 의료
        인공지능입니다.{"\n"}의료 정보 식별, 의학 문헌 탐색, 근거기반 질의응답,
        다국어 임상 언어 처리 등{"\n"} 각각 고유한 기능을 수행하는 다양한 AI가
        핵심 진단 AI와 유기적으로 협력하여 작동합니다.{"\n"}이를 통해 사용자는
        마치 의사와 대화하듯 자유롭게 증상을 이야기하고, 신뢰도 높은 건강 상담을
        받을 수 있습니다.
      </div>
    );
  };

  const getMobileDescription = () => {
    return (
      <div
        className="whitespace-pre-line text-center font-pre-medium text-[#2E323A] desktop:text-[20px] text-[14px] leading-[150%]"
        style={{ letterSpacing: "-0.02em" }}
      >
        CIERA는 실제 의료진의 사고 흐름을 모사하여 설계된{"\n"} 통합형 의료
        인공지능입니다. 의료 정보 식별, 의학 문헌 탐색,{"\n"}근거기반 질의응답,
        다국어 임상 언어 처리 등{"\n"}각각 고유한 기능을 수행하는 다양한 AI가
        핵심 진단 AI와{"\n"} 유기적으로 협력하여 작동합니다.{"\n"}이를 통해
        사용자는 마치 의사와 대화하듯 자유롭게{"\n"} 증상을 이야기하고, 신뢰도
        높은 건강 상담을 받을 수 있습니다.
      </div>
    );
  };

  const titleRef = useRef(null);
  const isTitleInView = useInView(titleRef, { once: true, amount: 0.0 });

  const w01Ref = useRef(null);
  const isW01InView = useInView(w01Ref, { once: true, amount: 0.5 });

  const w02Ref = useRef(null);
  const isW02InView = useInView(w02Ref, { once: true, amount: 0.5 });

  const titleVariants = {
    hidden: {
      opacity: 0,
      scale: 1,
      rotate: 0,
      skew: 0,
      x: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      skew: 0,
      x: 0,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 100,
        mass: 1,
        delay: 0.1,
      },
    },
  };

  return (
    <div className="flex flex-col items-center desktop:py-[120px] desktop:px-[32px] py-[80px] px-[24px] bg-[#F5F6FA]">
      <motion.div
        id="title"
        className="flex flex-col items-center"
        ref={titleRef}
        initial="hidden"
        animate={isTitleInView ? "visible" : "hidden"}
        variants={titleVariants}
      >
        <div
          className="font-pre-bold text-center desktop:text-[38px] text-[24px] leading-[150%]"
          style={{
            letterSpacing: "-0.02em",
            background: "linear-gradient(90deg, #5464F2 17.01%, #39969E 83.1%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          헬시어 AI 모델 CIERA
        </div>
        <Spacer height={3} />
        <div
          className="text-center text-[#787C83] font-pre-regular desktop:text-[18px] text-[12px] leading-[150%]"
          style={{ letterSpacing: "-0.02em" }}
        >
          Clinical Intelligence for Evidence-based Reasoning & Assistance
        </div>

        <Spacer height={isMobile ? 16 : 32} />
        {isMobile ? getMobileDescription() : getDesktopDescription()}
      </motion.div>

      <Spacer height={isMobile ? 80 : 120} />

      <div className="desktop:flex desktop:w-full dekstop:flex-row gap-[24px] desktop:max-w-[960px] hidden">
        <WithoutCiera />

        <WithCiera />
      </div>

      <div className="w-full flex flex-col items-center max-w-[342px] desktop:hidden">
        <motion.div
          id="w-01"
          className="flex flex-col items-center"
          ref={w01Ref}
          initial="hidden"
          animate={isW01InView ? "visible" : "hidden"}
          variants={titleVariants}
        >
          <div
            className="font-pre-medium text-[24px] leading-[1.2em] text-[#999DA4]"
            style={{ letterSpacing: "-0.02em" }}
          >
            Without CIERA
          </div>

          <Spacer height={20} />
          <CieraCard01 isMobile={true} />
          <Spacer height={50} />
          <div
            className="font-pre-bold text-[24px] leading-[1.2em]"
            style={{
              letterSpacing: "-0.02em",
              background:
                "linear-gradient(90deg, #5464F2 17.01%, #39969E 83.1%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            With CIERA
          </div>

          <Spacer height={20} />
          <CieraCard03 isMobile={true} />
        </motion.div>
        <Spacer height={50} />
        <motion.div
          id="w-02"
          className="flex flex-col items-center"
          ref={w02Ref}
          initial="hidden"
          animate={isW02InView ? "visible" : "hidden"}
          variants={titleVariants}
        >
          <div
            className="font-pre-medium text-[24px] leading-[1.2em] text-[#999DA4]"
            style={{ letterSpacing: "-0.02em" }}
          >
            Without CIERA
          </div>

          <Spacer height={20} />
          <CieraCard02 isMobile={true} />
          <Spacer height={50} />
          <div
            className="font-pre-bold text-[24px] leading-[1.2em]"
            style={{
              letterSpacing: "-0.02em",
              background:
                "linear-gradient(90deg, #5464F2 17.01%, #39969E 83.1%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            With CIERA
          </div>

          <Spacer height={20} />
          <CieraCard04 isMobile={true} />
        </motion.div>
      </div>
    </div>
  );
}

const CieraCard01 = ({ isMobile = false }: { isMobile?: boolean }) => {
  return (
    <div
      className="w-full desktop:h-[380px] h-[300px] rounded-[24px] bg-[#F5F6FA] flex flex-col items-center  justify-between pt-[32px] desktop:px-[18px] px-[8px]"
      style={{ boxShadow: "2px 2px 20px 0px rgba(116, 116, 116, 0.1)" }}
    >
      <div
        className="font-pre-semibold text-[#4F4F4F] leading-[1.2em] desktop:text-[20px] text-[18px]"
        style={{ letterSpacing: "-0.02em" }}
      >
        비개인화된 인터넷 기반 획일적 정보 제공
      </div>

      <img
        src={
          isMobile
            ? "assets/images/section-04-01-mobile.png"
            : "assets/images/section-04-01-desktop.png"
        }
        alt="CIERA 01"
        className="desktop:w-[430px] desktop:h-[276.5px] w-[335px] h-[214px]"
      />
    </div>
  );
};

const CieraCard02 = ({ isMobile = false }: { isMobile?: boolean }) => {
  return (
    <div
      className="w-full desktop:h-[420px] h-[320px] rounded-[24px] bg-[#F5F6FA] flex flex-col items-center  justify-between pt-[32px] desktop:px-[0px] px-[18px] desktop:pb-[20px] pb-[16px]"
      style={{ boxShadow: "2px 2px 20px 0px rgba(116, 116, 116, 0.1)" }}
    >
      <div
        className="font-pre-semibold text-[#4F4F4F] leading-[1.2em] desktop:text-[20px] text-[18px]"
        style={{ letterSpacing: "-0.02em" }}
      >
        고정된 질문 흐름이나 정형화된 선택지
      </div>

      <img
        src={
          isMobile
            ? "assets/images/section-04-02-mobile.png"
            : "assets/images/section-04-02-desktop.png"
        }
        alt="CIERA 02"
        className="desktop:w-[430px] desktop:h-[292.5px] w-[315px] h-[214.5px]"
      />
    </div>
  );
};

const CieraCard03 = ({ isMobile = false }: { isMobile?: boolean }) => {
  return (
    <motion.div
      className="w-full desktop:h-[380px] h-[300px] rounded-[24px] bg-[#ffffff] flex flex-col items-center justify-between pt-[32px] desktop:px-[0px] px-[18px] desktop:pb-[20px] pb-[16px]"
      style={{ boxShadow: "2px 2px 20px 0px rgba(116, 116, 116, 0.1)" }}
      whileHover={{
        opacity: 1,
        scale: 1.02,
        rotate: 0,
        skew: 0,
        x: 0,
        y: 0,
        boxShadow: "0px 2px 20px 0px rgba(82, 98, 242, 0.2)",
      }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 30,
        mass: 1,
        delay: 0,
      }}
    >
      <div
        className="font-pre-semibold text-[#4F4F4F] leading-[1.2em] desktop:text-[20px] text-[18px]"
        style={{ letterSpacing: "-0.02em" }}
      >
        의료진의 사고 과정을 모방한 건강 상태 분석
      </div>

      <img
        src={
          isMobile
            ? "assets/images/section-04-03-mobile.png"
            : "assets/images/section-04-03-desktop.png"
        }
        alt="CIERA 03"
        className="desktop:w-[430px] desktop:h-[248px] w-[315px] h-[181.5px]"
      />
    </motion.div>
  );
};

const CieraCard04 = ({ isMobile = false }: { isMobile?: boolean }) => {
  return (
    <motion.div
      className="w-full desktop:h-[420px] h-[340px] rounded-[24px] bg-[#ffffff] flex flex-col items-center justify-between pt-[32px] desktop:px-[0px] px-[18px] desktop:pb-[16px] pb-[16px]"
      style={{ boxShadow: "2px 2px 20px 0px rgba(116, 116, 116, 0.1)" }}
      whileHover={{
        opacity: 1,
        scale: 1.02,
        rotate: 0,
        skew: 0,
        x: 0,
        y: 0,
        boxShadow: "0px 2px 20px 0px rgba(82, 98, 242, 0.2)",
      }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 30,
        mass: 1,
        delay: 0,
      }}
    >
      <div
        className="font-pre-semibold text-[#4F4F4F] leading-[1.2em] desktop:text-[20px] text-[18px] whitespace-pre-line text-center"
        style={{ letterSpacing: "-0.02em" }}
      >
        핵심 의료 정보를 자동 추출하는{isMobile ? "\n" : ""} 자유로운 문진
      </div>

      <video
        src="/assets/videos/section-04-04.mp4"
        autoPlay
        loop
        muted
        playsInline
        className="desktop:w-[430px] desktop:h-[293px] h-[214.5px]"
      />
    </motion.div>
  );
};

const WithoutCiera = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { amount: 0.0, once: true });

  const variants = {
    hidden: {
      opacity: 0,
      scale: 1,
      rotate: 0,
      x: 0,
      y: 30,
    },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      x: 0,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 100,
        mass: 1,
        delay: 0.25,
      },
    },
  };

  return (
    <motion.div
      id="without-ciera"
      className="w-full flex flex-col items-center"
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={variants}
    >
      <div
        className="font-pre-medium text-[24px] leading-[1.2em] text-[#999DA4]"
        style={{ letterSpacing: "-0.02em" }}
      >
        Without CIERA
      </div>

      <Spacer height={32} />
      <CieraCard01 isMobile={false} />
      <Spacer height={24} />
      <CieraCard02 isMobile={false} />
    </motion.div>
  );
};

const WithCiera = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { amount: 0.5, once: true });

  const variants = {
    hidden: {
      opacity: 0,
      scale: 1,
      rotate: 0,
      x: 0,
      y: 30,
    },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      x: 0,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 100,
        mass: 1,
        delay: 0.25,
      },
    },
  };

  return (
    <motion.div
      id="with-ciera"
      className="w-full flex flex-col items-center"
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={variants}
    >
      <div
        className="font-pre-bold text-[24px] leading-[1.2em]"
        style={{
          letterSpacing: "-0.02em",
          background: "linear-gradient(90deg, #5464F2 17.01%, #39969E 83.1%)",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        With CIERA
      </div>

      <Spacer height={32} />
      <CieraCard03 isMobile={false} />

      <Spacer height={24} />
      <CieraCard04 isMobile={false} />
    </motion.div>
  );
};
